import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import { videos } from '@/mocks/videos';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const videoId = parseInt(id || '0', 10);

  const video = videos.find((v) => v.id === videoId);
  const article = articles.find((a) => a.id === videoId);

  const relatedVideos = videos
    .filter((v) => v.id !== videoId && v.category === video?.category)
    .slice(0, 3);

  const relatedArticles = articles
    .filter((a) => a.id !== videoId)
    .slice(0, 3);

  if (!video && !article) {
    return (
      <>
        <Navbar />
        <div className="pt-20 text-center py-20">
          <p className="text-foreground-500">内容不存在</p>
        </div>
        <Footer />
      </>
    );
  }

  const displayContent = article || video;
  const pageTitle = displayContent?.title || '';
  const pageDescription = article ? article.excerpt : video?.description || '';

  const articleSchema = article ? {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    image: article.coverImage,
    publisher: {
      '@type': 'Organization',
      name: '桃子视频',
    },
  } : undefined;

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDescription}
        keywords={`${pageTitle},桃子视频,在线观看,高清视频`}
        canonicalUrl={`https://taozivideo.com/article/${id}`}
        ogType="article"
        schema={articleSchema}
      />
      <Navbar />
      <main className="pt-16">
        <div className="w-full bg-background-100 border-b border-background-200/70">
          <div className="w-full px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                ...(video ? [{ label: video.categoryName, href: `/category/${video.category}` }] : []),
                { label: pageTitle },
              ]}
            />
          </div>
        </div>

        <article className="w-full px-4 md:px-6 py-8 md:py-12">
          <div className="max-w-4xl mx-auto">
            {video && !article && (
              <>
                <div className="relative w-full aspect-video rounded-xl overflow-hidden mb-8">
                  <img
                    src={video.thumbnail}
                    alt={video.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                    <div className="w-20 h-20 flex items-center justify-center rounded-full bg-primary-500/90 text-white cursor-pointer hover:bg-primary-600 transition-colors">
                      <i className="ri-play-fill text-3xl"></i>
                    </div>
                  </div>
                  <div className="absolute top-4 left-4 flex items-center gap-2">
                    <span className="px-3 py-1 rounded-full bg-black/60 text-white text-sm font-medium backdrop-blur-sm whitespace-nowrap">
                      {video.quality}
                    </span>
                    <span className="px-3 py-1 rounded-full bg-primary-500/80 text-white text-sm font-medium backdrop-blur-sm whitespace-nowrap">
                      {video.rating}分
                    </span>
                  </div>
                </div>

                <header className="mb-8">
                  <div className="flex items-center gap-2 mb-3 flex-wrap">
                    <span className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium whitespace-nowrap">
                      {video.categoryName}
                    </span>
                    {video.tags.map((tag) => (
                      <span key={tag} className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs whitespace-nowrap">
                        {tag}
                      </span>
                    ))}
                  </div>
                  <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4">
                    {video.title}
                  </h1>
                  <div className="flex items-center gap-4 text-sm text-foreground-400 flex-wrap">
                    <span className="whitespace-nowrap">年份：{video.year}</span>
                    <span className="whitespace-nowrap">时长：{video.duration}</span>
                    <span className="whitespace-nowrap flex items-center gap-1">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-eye-line text-sm"></i>
                      </div>
                      {video.views}次播放
                    </span>
                  </div>
                </header>

                <section className="prose-custom mb-12">
                  <h2 className="text-xl font-heading font-semibold text-foreground-900 mb-4">视频简介</h2>
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                    {video.description}
                  </p>
                </section>
              </>
            )}

            {article && (
              <>
                <div className="relative w-full aspect-[2/1] rounded-xl overflow-hidden mb-8">
                  <img
                    src={article.coverImage}
                    alt={article.title}
                    className="w-full h-full object-cover"
                  />
                </div>

                <header className="mb-8">
                  <div className="flex items-center gap-2 mb-3 flex-wrap">
                    <span className="px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium whitespace-nowrap">
                      {article.category}
                    </span>
                    {article.tags.map((tag) => (
                      <span key={tag} className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs whitespace-nowrap">
                        {tag}
                      </span>
                    ))}
                  </div>
                  <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4">
                    {article.title}
                  </h1>
                  <div className="flex items-center gap-4 text-sm text-foreground-400 flex-wrap">
                    <span className="whitespace-nowrap">{article.author}</span>
                    <span className="whitespace-nowrap">{article.date}</span>
                    <span className="whitespace-nowrap">{article.readTime}</span>
                    <span className="whitespace-nowrap flex items-center gap-1">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-eye-line text-sm"></i>
                      </div>
                      {article.views}次阅读
                    </span>
                  </div>
                </header>

                <section
                  className="prose-custom mb-12 text-sm md:text-base text-foreground-700 leading-relaxed space-y-4"
                  dangerouslySetInnerHTML={{ __html: article.content }}
                />
              </>
            )}

            {video && !article && relatedVideos.length > 0 && (
              <section className="border-t border-background-200/70 pt-10">
                <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-6">相关推荐</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-product-shop="">
                  {relatedVideos.map((v) => (
                    <Link
                      key={v.id}
                      to={`/article/${v.id}`}
                      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                      className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-300 transition-all cursor-pointer"
                    >
                      <div className="relative w-full aspect-video overflow-hidden">
                        <img
                          src={v.thumbnail}
                          alt={v.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-3">
                        <h4 className="text-sm font-medium text-foreground-900 line-clamp-1">{v.title}</h4>
                        <p className="text-xs text-foreground-400 mt-1">{v.rating}分 · {v.views}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}

            {article && relatedArticles.length > 0 && (
              <section className="border-t border-background-200/70 pt-10">
                <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-6">相关文章</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {relatedArticles.map((a) => (
                    <Link
                      key={a.id}
                      to={`/article/${a.id}`}
                      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                      className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-300 transition-all cursor-pointer"
                    >
                      <div className="relative w-full aspect-[2/1] overflow-hidden">
                        <img
                          src={a.coverImage}
                          alt={a.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-3">
                        <h4 className="text-sm font-medium text-foreground-900 line-clamp-1">{a.title}</h4>
                        <p className="text-xs text-foreground-400 mt-1">{a.date} · {a.readTime}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}